import React from 'react';
import { motion } from 'framer-motion';
import type { SiteMetric } from '../types';
import { TrendLine } from './TrendLine';

interface SiteRankingTableProps {
    sites: SiteMetric[];
    isLoading: boolean;
}

const statusConfig = {
    excellent: { label: 'Отлично', color: 'text-[var(--status-up)]', bg: 'bg-[var(--status-up-bg)]' },
    good: { label: 'Хорошо', color: 'text-[var(--brand)]', bg: 'bg-[var(--brand-dim)]' },
    warning: { label: 'Предупреждение', color: 'text-[var(--status-warn)]', bg: 'bg-[var(--status-warn-bg)]' },
    critical: { label: 'Критично', color: 'text-[var(--status-down)]', bg: 'bg-[var(--status-down-bg)]' },
};

const getStatus = (uptime: number) => {
    if (uptime >= 99.9) return 'excellent';
    if (uptime >= 99) return 'good';
    if (uptime >= 95) return 'warning';
    return 'critical';
};

export const SiteRankingTable: React.FC<SiteRankingTableProps> = ({ 
    sites, 
    isLoading 
}) => {
    if (isLoading) {
        return (
            <div className="mx-[18px] h-[200px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl animate-pulse" />
        );
    }

    const sortedSites = [...sites].sort((a, b) => b.uptime - a.uptime);

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mx-[18px] bg-[var(--bg-elevated)] border border-[var(--border)] rounded-xl overflow-hidden"
        >
            <div className="px-4 py-3 border-b border-[var(--border)]">
                <h3 className="text-[13px] font-medium text-[var(--text)]">Рейтинг сайтов</h3>
            </div>
            
            <div className="divide-y divide-[var(--border)]">
                {sortedSites.map((site, index) => {
                    const status = statusConfig[getStatus(site.uptime)];
                    const sparkline = Array.from({ length: 20 }, () => 
                        site.uptime + (Math.random() - 0.5) * 2
                    );
                    
                    return (
                        <motion.div
                            key={site.siteId}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="flex items-center gap-4 px-4 py-3 hover:bg-[var(--bg-hover)] transition-colors cursor-pointer"
                        >
                            <span className="w-6 text-center text-[12px] font-medium text-[var(--text-muted)]">
                                {index + 1}
                            </span>
                            
                            <div className="flex-1 min-w-0">
                                <div className="text-[13px] font-medium text-[var(--text)] truncate">
                                    {site.siteName}
                                </div>
                                <div className="text-[11px] text-[var(--text-muted)]">
                                    {site.totalRequests.toLocaleString('ru-RU')} запросов · {site.incidents} инцидентов
                                </div>
                            </div>
                            
                            <div className="w-16 h-8 hidden sm:block">
                                <TrendLine 
                                    data={sparkline} 
                                    color={site.uptime >= 99 ? 'success' : site.uptime >= 95 ? 'brand' : 'warning'} 
                                />
                            </div>
                            
                            <div className="text-right">
                                <div className={`font-mono text-[15px] font-medium ${status.color}`}>
                                    {site.uptime.toFixed(2)}%
                                </div>
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] ${status.bg} ${status.color}`}>
                                    {status.label}
                                </span>
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </motion.div>
    );
};